import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { Formik, FormikProps } from 'formik';
import { useMutation } from '@apollo/client';

import { handleValidationErrors, onCompletedHandler, onErrorHandler } from '@/services/apollo';
import { initializedAtom, userAtom } from '@/components/with_auth/State';
import routes from '@/services/routes';
import { UPDATE_USER } from '@/components/onboarding/Actions';
import {
    BIO_NAMES,
    haveErrors,
    birthdayFormat,
    reportGeneralError,
    getNewOnboardingState,
} from '@/components/onboarding/Shared';
import { getDateParts, getLocalTimezone, timezones } from '@/hooks/calendar_helpers';
import PanelContainer from '@/components/commons/panel_container';
import { type bioPageProps } from '@/types/globals';
import useIsEdit from '@/hooks/use_is_edit';
import { type UpdateUserAttributesType } from '@/components/settings/Actions';

import NavButtons from './NavigationButtons';
import BioPageHeader from './BioPageHeader';
import PersonalizeForm from './PersonalizeForm';
import bioStyles from './Bio.module.css';

// used to store data from localstorage
interface userdata {
    fullName: string;
    year?: string;
    month?: string;
    day?: string;
    company?: string;
    title?: string;
    linkedIn?: string;
}

function PersonalizePanel({
    isModal = false,
    extendContinue = null,
    overrideBack = null,
    isEdit,
}: bioPageProps) {
    const router = useRouter();
    const [selectedMonth, setSelectedMonth] = useState('01');
    const [selectedDay, setSelectedDay] = useState('01');
    const [selectedYear, setSelectedYear] = useState(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [tempUserData, setTempUserData] = useState<userdata>();
    const [timezone, setTimezone] = useState<number>();

    const user = useRecoilValue(userAtom);

    useEffect(() => {
        if (user) {
            const {
                timezoneName,
                timezoneOffset,
            } = user;
            let timezoneIndex = null;
            for (let i = 0; i < timezones.length; i++) {
                if (timezones[i][0] === timezoneName && timezones[i][1] === timezoneOffset) {
                    timezoneIndex = i;
                    break;
                }
            }
            if (!timezoneIndex) {
                timezoneIndex = getLocalTimezone();
            }

            if (timezoneIndex) {
                setTimezone(timezoneIndex);
            }
        }
    }, [user]);

    const setters = {
        setUser: useSetRecoilState(userAtom),
        setInitialized: useSetRecoilState(initializedAtom),
    };

    const formRef = useRef<FormikProps<any>>(null);
    const linkedInFormRef = useRef<any>(null);
    const [updateUser] = useMutation(UPDATE_USER);

    const { url } = useIsEdit();

    const handleContinue = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const handleBackNavigation = () => {
        if (isModal && overrideBack) {
            overrideBack(false);
        } else {
            router.push(routes.BIO_AUTHORIZE);
        }
    };

    const handleMonthSelect = (newSelectedMonth) => {
        setSelectedMonth(newSelectedMonth);
    };

    const handleDaySelect = (newSelectedDay) => {
        if (selectedDay !== newSelectedDay) { setSelectedDay(newSelectedDay); }
    };

    const handleYearSelect = (newSelectedYear) => {
        if (selectedYear !== newSelectedYear) { setSelectedYear(newSelectedYear); }
    };

    const handleUserUpdate = async (
        attributes: Partial<UpdateUserAttributesType>,
        setSubmitting,
        setStatus,
    ) => {
        setIsLoading(true);
        await updateUser({
            variables: {
                updateUserInput: {
                    attributes,
                },
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'no-cache',
            onError: onErrorHandler((error) => {
                setSubmitting(false);
                reportGeneralError(error);
            }),
            onCompleted: onCompletedHandler(() => { }, (data) => {
                setSubmitting(false);
                const { errors } = data.updateUser;
                if (haveErrors(errors)) {
                    handleValidationErrors(errors, setStatus, 'user');
                    setIsLoading(false);
                    return;
                }

                setters.setUser(data.updateUser.user);
                setters.setInitialized(true);
                setStatus({ errors: {} });
                setIsLoading(false);
                if (url) {
                    router.push(url);
                } else if (isModal && extendContinue) {
                    extendContinue('', isEdit);
                } else {
                    router.push(routes.BIO_CAREER_HISTORY);
                }
            }),
        });
    };

    useEffect(() => {
        const userDataFromLocalStorage = localStorage.getItem('userInformation');
        setTempUserData(JSON.parse(userDataFromLocalStorage));
    }, []);

    useEffect(() => {
        const handleBeforeUnload = () => {
            if (formRef && formRef?.current?.values) {
                localStorage.setItem('userInformation', JSON.stringify(formRef.current.values));
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
    }, []);

    return (
        <PanelContainer stepNames={BIO_NAMES} paddingOverride="p-0 sm:p-8" step={0} totalSteps={5} image="/images/bio/bio_personalize.png" isModal={isModal}>
            <form ref={linkedInFormRef} className="w-min hidden" action={`${process.env.NEXT_PUBLIC_BACKEND_ROOT}/auth/linkedin`} method="post">
                <input type="hidden" name="target" value={`${process.env.NEXT_PUBLIC_FRONTEND_ROOT}${routes.BIO_PERSONALIZE}`} />
                <input type="submit" className="text-progressBarBlue no-underline cursor-pointer" value="Sign in to LinkedIn" />
            </form>
            <div className="w-full py-2 px-2 sm:px-5">
                <BioPageHeader
                    header="Welcome to Sixty. Let&apos;s get started."
                    subHeader="We use AI and a few inputs from you to create a brief with all the relevant info about you in one place. To do it, we'll ask you a few questions about your professional experience and working preferences. Write in your normal voice (we'll provide the polish!) and share as much or as little as you like. The more you share, the better prepared people will be to meet with you."
                    subHeaderWidth="max-w-[677px]"
                />
                <div className="flex flex-col md:flex-row space-between gap-2 lg:gap-8">
                    <div className="w-full sm:ml-0">
                        <Formik
                            innerRef={formRef}
                            enableReinitialize
                            onSubmit={(frm, { setStatus, setSubmitting }) => {
                                if (!frm.fullName) {
                                    setStatus({ errors: { fullName: ['Please enter a preferred name'] } });
                                    setSubmitting(false);
                                    return;
                                }

                                const [birthday, err] = birthdayFormat(
                                    frm.month,
                                    frm.day,
                                    frm.year,
                                );
                                if (err) {
                                    setStatus({ errors: { birthday: [err] } });
                                    setSubmitting(false);
                                    return;
                                }

                                const attributes: any = {
                                    fullName: frm.fullName?.trim(),
                                    birthday,
                                    title: frm.title?.trim(),
                                    company: frm.company?.trim(),
                                    socialLinks: {
                                        ...user?.socialLinks,
                                        linkedIn: frm.linkedIn,
                                    },
                                    timezoneName: frm.timezone && timezones[frm.timezone][0],
                                    timezoneOffset: frm.timezone && timezones[frm.timezone][1],
                                };
                                if (!isModal) {
                                    attributes.onboardingState = getNewOnboardingState(user, 1);
                                }
                                handleUserUpdate(attributes, setSubmitting, setStatus);
                            }}
                            initialValues={{
                                ...getDateParts(user.birthday ? user.birthday : birthdayFormat(
                                    tempUserData?.month,
                                    tempUserData?.day,
                                    tempUserData?.year,
                                )[0]),
                                fullName: user.fullName || tempUserData?.fullName || '',
                                linkedIn: user?.socialLinks?.linkedIn || tempUserData?.linkedIn || '',
                                title: user?.title || tempUserData?.title || '',
                                company: user?.company || tempUserData?.company || '',
                                timezone,
                            }}
                        >
                            {({ status, setStatus, handleChange }) => (
                                <PersonalizeForm
                                    status={status}
                                    handleMonthSelect={handleMonthSelect}
                                    handleDaySelect={handleDaySelect}
                                    handleYearSelect={handleYearSelect}
                                    setStatus={setStatus}
                                    handleChange={handleChange}
                                    selectedMonth={selectedMonth}
                                    buttonRef={linkedInFormRef}
                                />
                            )}
                        </Formik>
                    </div>
                </div>
                <NavButtons
                    isLoading={isLoading}
                    containerStyles={bioStyles.buttonGroupWithoutBio}
                    handleBackNavigation={handleBackNavigation}
                    handleContinue={handleContinue}
                    isEdit={url || isEdit}
                />
            </div>
        </PanelContainer>
    );
}

export default PersonalizePanel;
